import type { ReactNode } from "react";

/**
 * Chrome around a product visual.
 *
 * `badge` labels what the visitor is looking at. It defaults to the
 * marketing-page case ("Demo data") and must be overridden anywhere the
 * frame wraps real output, where calling it demo data would be false.
 * Pass `null` to hide the badge entirely.
 *
 * `hint` says what a visitor can do with the screen inside. Visitors were not
 * discovering that these are the running product rather than screenshots, so a
 * framed screen that can be explored says so — in the badge and in one line of
 * plain text under the frame.
 */
export function BrowserFrame({
  url,
  badge,
  hint,
  children,
}: {
  url: string;
  badge?: string | null;
  hint?: string;
  children: ReactNode;
}) {
  const label = badge === undefined ? (hint ? "Live demo" : "Demo data") : badge;
  return (
    <div className="w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-xl dark:border-slate-700 dark:bg-slate-950">
      <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-100 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900">
        <div className="flex gap-1.5" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        </div>
        <div className="min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-3 py-1 text-[10px] text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
          <span className="truncate">{url}</span>
        </div>
        {label ? (
          <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
            {label}
          </span>
        ) : null}
      </div>
      <div className="relative">{children}</div>
      {hint ? (
        <p
          className="border-t border-slate-200 bg-slate-100 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400"
          data-testid="text-demo-hint"
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}
